// useReportCtx(job) → { ctx, variant, … }     the report's shared context (DESIGN_SPEC §11.4, §16 contract)
// job record + its SSE stream (effective state) + artifacts + static-report.json + behavior-report.json
// (after the run; always same-origin) + the cite index (documents only) + the OS-background index.
// The page variant comes from the EFFECTIVE state, never the record:
//   queued → "queued" · dispatched…collecting → "live" · completed → "document" · failed → "failed"
//   cancelled → "document" (partial: it ran, or anything was recorded) | "cancelled-before" (never dispatched)
// A stream without a verdict (real records carry none) falls back to behavior-report.json's verdict.
// While the run is live the stream the figures read is refreshed at most once a second, and less often past 10,000
// events (liveFrameMs): every live derivation (facts, tree, fold, ledger) is linear in the run's events, so a busy run
// must not redo them per frame. State, connection, verdict and the end pass through at once. The background index
// itself is folded incrementally (createNoiseTracker).
// When the run ends the artifact list is fetched at once and then every 3 s for a minute, until the node has stored
// what collection produces (behavior-report.json; replay.mp4 for a completed run).
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { keys, pickArtifact, useArtifacts, useBehaviorReport, useJobStream, useStaticReport } from "@/hooks";
import type { ArtifactSettle } from "@/hooks/useArtifacts";
import { citeIndex } from "@/lib/derive/cite";
import { createNoiseTracker } from "@/lib/derive/noise";
import type { CiteIndex, JobStream, ReportCtx } from "@/lib/derive/types";
import { reportVerdict } from "@/lib/derive/verdict";
import { isTerminal } from "@/lib/eventStore";
import type { Artifact, Job, Tag } from "@/types";
import { variantOf } from "./facts";

export type { Variant } from "./facts";

const NO_ARTIFACTS: Artifact[] = [];
const NO_TAGS: Tag[] = [];
const NO_CITES: CiteIndex = new Map();
const SETTLE_MS = 60_000;

/** 1 s, growing by 1 s per 10,000 events up to 5 s. */
export const liveFrameMs = (events: number) => Math.min(5000, Math.max(1000, Math.round(events / 10)));

/** The live stream at most once per `ms` (trailing); anything that changes what the page is passes through at once. */
function useLiveFrame(raw: JobStream, ms: number): JobStream {
  const [shown, setShown] = useState(raw);
  const last = useRef(0);
  const urgent =
    !raw.live ||
    raw.state !== shown.state ||
    raw.connection !== shown.connection ||
    raw.ended !== shown.ended ||
    raw.verdict !== shown.verdict ||
    raw.t0 !== shown.t0 ||
    raw.recordLags !== shown.recordLags ||
    raw.stateSource !== shown.stateSource;
  useEffect(() => {
    if (raw === shown) return;
    const wait = urgent ? 0 : Math.max(0, last.current + ms - Date.now());
    const t = window.setTimeout(() => {
      last.current = Date.now();
      setShown(raw);
    }, wait);
    return () => window.clearTimeout(t);
  }, [raw, shown, urgent, ms]);
  return urgent ? raw : shown;
}

/** When the run ended (epoch ms): the stream's terminal step, else the record's ended_at. */
function endOf(stream: JobStream, job: Job): number | null {
  for (let i = stream.transitions.length - 1; i >= 0; i--) {
    const t = stream.transitions[i];
    if (isTerminal(t.state) && Number.isFinite(t.ts)) return t.ts;
  }
  const e = Date.parse(job.ended_at ?? "");
  return Number.isFinite(e) ? e : null;
}

export function useReportCtx(job: Job) {
  const qc = useQueryClient();
  const raw = useJobStream(job.id, job);
  const variant = variantOf(raw, job);

  // the artifacts collection stores: fetch at the end, then settle (completed runs also get their replay)
  const wasLive = useRef(raw.live);
  const [endedHere, setEndedHere] = useState<number | null>(null);
  useEffect(() => {
    if (wasLive.current && !raw.live) {
      setEndedHere(Date.now());
      void qc.invalidateQueries({ queryKey: keys.artifacts(job.id) });
    }
    wasLive.current = raw.live;
  }, [raw.live, qc, job.id]);
  const endAt = raw.live ? null : (endedHere ?? endOf(raw, job));
  const completed = raw.state === "completed";
  const settle = useMemo<ArtifactSettle | null>(
    () =>
      endAt === null
        ? null
        : { until: endAt + SETTLE_MS, done: (list) => !!pickArtifact(list, "behavior-report") && (!completed || !!pickArtifact(list, "replay")) },
    [endAt, completed],
  );
  const artifactsQ = useArtifacts(job.id, { live: raw.live, settle });
  const artifacts = artifactsQ.data ?? NO_ARTIFACTS;
  const staticQ = useStaticReport(job.id, artifacts);
  const behaviorQ = useBehaviorReport(job.id, artifacts, { enabled: !raw.live });
  const behaviorReport = behaviorQ.data;

  const framed = useLiveFrame(raw, liveFrameMs(raw.snap.events.length));
  const stream = useMemo<JobStream>(() => {
    const verdict = reportVerdict(framed, behaviorReport);
    return verdict === framed.verdict ? framed : { ...framed, verdict };
  }, [framed, behaviorReport]);

  const snap = framed.snap;
  const tracker = useMemo(() => createNoiseTracker(), [job.id]);
  const noise = useMemo(() => tracker.update(snap.events), [tracker, snap]);
  const mode = variant === "live" ? "live" : "document";
  const tags = stream.verdict?.tags ?? NO_TAGS;
  const cite = useMemo(() => (mode === "document" && tags.length ? citeIndex(tags, snap.events) : NO_CITES), [mode, tags, snap]);

  const ctx = useMemo<ReportCtx>(
    () => ({ job, stream, artifacts, staticReport: staticQ.data, behaviorReport, cite, mode, noise }),
    [job, stream, artifacts, staticQ.data, behaviorReport, cite, mode, noise],
  );
  return { ctx, variant, tags, artifactsQ, staticQ, behaviorQ };
}
